/**
 * Reading a chain of title back and saying what is wrong with it.
 *
 * This is the one thing the drawing tool can do that a drawing tool
 * cannot. The document already holds who conveyed what to whom, in
 * order — so the questions an examiner works through by hand can be
 * asked of it: does everybody who granted have something to grant, does
 * anybody convey before they took title, does anybody convey more than
 * they got, and where does the chain simply stop.
 *
 * IT READS THE GRAPH, NOT THE METADATA. A chart seeded from a runsheet
 * carries a sourceKind on every box and it would be easy to lean on
 * that — and useless, because the charts that most need checking are the
 * ones brought across from Lucid and drawn by hand, which carry nothing.
 * So an instrument is recognised by having parties on both sides of it
 * and by reading like an instrument, and a party is whatever is left.
 *
 * IT ONLY SAYS WHAT IT CAN SHOW. A deed with no legible fraction is not
 * evidence of anything, so it produces no finding about fractions; a
 * date that cannot be read produces no finding about order. A checker
 * that guesses is a checker people learn to ignore, and one nobody
 * trusts is worse than none — an examiner who dismisses a true finding
 * because the last four were noise is worse off than one who read the
 * chain themselves.
 */

/** Words that mark a box as the instrument rather than a party to it. */
const INSTRUMENT_WORDS = new RegExp([
  'deed', 'conveyance', 'assignment', 'lease', 'patent', 'probate',
  'affidavit', 'decree', 'judgment', 'judgement', 'will', 'testament',
  'release', 'quitclaim', 'quit claim', 'warranty', 'mortgage',
  'easement', 'contract', 'order', 'grant',
].join('|'), 'i');

// Vol 412 Pg 88, Book 12 Page 3, Inst. 2019-004412, Doc No 55123.
const RECORDING = /\b(?:vol|volume|bk|book|pg|page|inst|instrument|doc|document|entry|recept(?:ion)?|file)\b\.?\s*#?\s*[\d-]/i;

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/**
 * Month names as they are actually written on a chart: in full, or cut
 * to three letters with or without a stop, or "Sept".
 *
 * Whole tokens rather than a prefix and a wildcard. A prefix would read
 * "Maryann 1962" as March, and a party's name turning into a date is a
 * finding invented out of nothing.
 */
const MONTH_WORDS = [...MONTHS, 'sept', ...MONTHS.map((m) => m.slice(0, 3))].join('|');

const ORDER = { year: 0, month: 1, day: 2 };

/**
 * A date out of whatever somebody wrote.
 *
 * The precision comes back with it, because a box that says "1962" and a
 * box that says "3 March 1962" cannot be put in order at the same
 * resolution, and pretending otherwise invents a finding out of a
 * missing month.
 */
export function parseDate(text) {
  const said = String(text || '');

  const iso = /\b(\d{4})-(\d{2})-(\d{2})\b/.exec(said);
  if (iso) return { iso: `${iso[1]}-${iso[2]}-${iso[3]}`, precision: 'day' };

  const slashed = /\b(\d{1,2})\/(\d{1,2})\/(\d{2,4})\b/.exec(said);
  if (slashed) {
    const year = slashed[3].length === 2
      // A two-digit year in a chain of title is almost never this
      // century: these documents run from the 1800s to now, so 62 is
      // 1962 and 05 is 2005.
      ? String(Number(slashed[3]) > 30 ? 1900 + Number(slashed[3]) : 2000 + Number(slashed[3]))
      : slashed[3].padStart(4, '0');
    return {
      iso: `${year}-${slashed[1].padStart(2, '0')}-${slashed[2].padStart(2, '0')}`,
      precision: 'day',
    };
  }

  const named = new RegExp(
    // The word boundary goes BEFORE the optional stop, not after it:
    // there is no boundary between "." and the space that follows, so
    // "Mar. 1962" matched nothing and fell through to the bare year.
    `\\b(${MONTH_WORDS})\\b\\.?[\\s,]*(\\d{1,2})?(?:st|nd|rd|th)?[\\s,]*(\\d{4})\\b`, 'i',
  ).exec(said);
  if (named) {
    const cut = named[1].toLowerCase().slice(0, 3);
    const month = String(MONTHS.findIndex((m) => m.startsWith(cut)) + 1).padStart(2, '0');
    if (!named[2]) return { iso: `${named[3]}-${month}-01`, precision: 'month' };
    return { iso: `${named[3]}-${month}-${named[2].padStart(2, '0')}`, precision: 'day' };
  }

  // A bare year, which a great many chart boxes carry and nothing else.
  const year = /\b(1[6-9]\d{2}|20\d{2})\b/.exec(said);
  if (year) return { iso: `${year[1]}-01-01`, precision: 'year' };

  return null;
}

/**
 * Which of two dates came first, at the coarser of their precisions.
 *
 * Returns -1, 0 or 1, and 0 whenever they cannot be told apart — a deed
 * dated 1962 and a deed dated 3 March 1962 are not out of order, they
 * are the same year and nothing more can be said.
 */
export function compareDates(a, b) {
  if (!a || !b) return 0;
  const cut = [4, 7, 10][Math.min(ORDER[a.precision], ORDER[b.precision])];
  const left = a.iso.slice(0, cut);
  const right = b.iso.slice(0, cut);
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

/**
 * The share a deed moved, as a number, or null when it does not say.
 *
 * The date guard is the whole difficulty: "WD 3/14/1962" is not three
 * fourteenths, and a checker that read it that way would report every
 * deed in the file as conveying a strange fraction.
 */
export function parseInterest(text) {
  const said = String(text || '');
  if (/\b(?:all|entire|whole|100\s*%|fee simple)\b/i.test(said)) return 1;

  const percent = /(\d{1,3}(?:\.\d+)?)\s*%/.exec(said);
  if (percent) return Number(percent[1]) / 100;

  const fractions = said.matchAll(/(\d{1,4})\s*\/\s*(\d{1,4})/g);
  for (const found of fractions) {
    const whole = said.slice(Math.max(0, found.index - 5), found.index + found[0].length + 5);
    // Part of a date rather than a share of the land.
    if (/\d+\s*\/\s*\d+\s*\/\s*\d/.test(whole)) continue;
    const top = Number(found[1]);
    const bottom = Number(found[2]);
    if (!bottom || top > bottom) continue;
    return top / bottom;
  }
  return null;
}

/** Does this box read like the instrument rather than a party to it? */
export function looksLikeInstrument(shape) {
  if (shape.sourceKind === 'deed') return true;
  const text = String(shape.text || '');
  if (!text.trim()) return false;
  return INSTRUMENT_WORDS.test(text) || RECORDING.test(text) || Boolean(parseDate(text));
}

/**
 * The chain as parties and conveyances, out of the drawing.
 *
 * Two idioms both mean the same thing and both have to work: a box for
 * the deed with an arrow either side of it, which is what a runsheet
 * builds, and a plain arrow from one party to the next with the deed
 * typed on the line, which is what somebody drawing by hand does.
 */
export function chainOf(doc) {
  const shapes = doc?.shapes || [];
  const edges = (doc?.edges || []).filter((e) => e.from?.shapeId && e.to?.shapeId);
  const byId = new Map(shapes.map((s) => [s.id, s]));

  const into = new Map();
  const outOf = new Map();
  for (const edge of edges) {
    if (!byId.has(edge.from.shapeId) || !byId.has(edge.to.shapeId)) continue;
    if (!into.has(edge.to.shapeId)) into.set(edge.to.shapeId, []);
    if (!outOf.has(edge.from.shapeId)) outOf.set(edge.from.shapeId, []);
    into.get(edge.to.shapeId).push(edge);
    outOf.get(edge.from.shapeId).push(edge);
  }

  // An instrument needs parties on BOTH sides. A box that reads like a
  // deed but hangs off the end of the chain is a note somebody left, not
  // a conveyance, and treating it as one invents a broken link.
  const instruments = new Set(
    shapes
      .filter((s) => into.has(s.id) && outOf.has(s.id) && looksLikeInstrument(s))
      .map((s) => s.id),
  );

  const parties = shapes.filter((s) => !instruments.has(s.id));
  const conveyances = [];

  for (const id of instruments) {
    const shape = byId.get(id);
    const from = (into.get(id) || []).map((e) => e.from.shapeId).filter((p) => !instruments.has(p));
    const to = (outOf.get(id) || []).map((e) => e.to.shapeId).filter((p) => !instruments.has(p));
    conveyances.push({
      id, shapeId: id, text: shape.text || '', from, to,
      date: parseDate(shape.text), interest: parseInterest(shape.text),
    });
  }

  // A line straight from one party to another carries the deed on itself.
  for (const edge of edges) {
    if (instruments.has(edge.from.shapeId) || instruments.has(edge.to.shapeId)) continue;
    if (!byId.has(edge.from.shapeId) || !byId.has(edge.to.shapeId)) continue;
    conveyances.push({
      id: edge.id,
      edgeId: edge.id,
      text: edge.label || '',
      from: [edge.from.shapeId],
      to: [edge.to.shapeId],
      date: parseDate(edge.label),
      interest: parseInterest(edge.label),
    });
  }

  return { parties, instruments, conveyances, byId };
}

/** What a box is called, for a sentence somebody has to read. */
export function nameOf(shape) {
  const first = String(shape?.text || '').split('\n').map((s) => s.trim()).find(Boolean);
  return first || 'an unnamed box';
}

/** Every party that can be reached by following conveyances out of one. */
function cycleThrough(parties, conveyances) {
  const next = new Map(parties.map((p) => [p.id, []]));
  for (const c of conveyances) {
    for (const from of c.from) {
      for (const to of c.to) {
        if (next.has(from)) next.get(from).push(to);
      }
    }
  }
  const state = new Map();
  const found = [];
  const walk = (id, path) => {
    if (state.get(id) === 'done') return;
    if (state.get(id) === 'open') {
      found.push(path.slice(path.indexOf(id)));
      return;
    }
    state.set(id, 'open');
    for (const to of next.get(id) || []) walk(to, [...path, to]);
    state.set(id, 'done');
  };
  for (const party of parties) walk(party.id, [party.id]);
  return found;
}

const EPSILON = 1e-6;

/**
 * Everything worth saying about a chain, worst first.
 *
 * Each finding carries the ids it is about so the canvas can show them:
 * a list of complaints with no way to find what they are complaining
 * about is a list nobody works through.
 */
export function checkChain(doc) {
  const { parties, conveyances, byId } = chainOf(doc);
  const findings = [];
  const say = (kind, level, message, ids) => findings.push({ kind, level, message, ids });

  const took = new Map(parties.map((p) => [p.id, []]));
  const gave = new Map(parties.map((p) => [p.id, []]));
  for (const c of conveyances) {
    for (const to of c.to) if (took.has(to)) took.get(to).push(c);
    for (const from of c.from) if (gave.has(from)) gave.get(from).push(c);
  }

  // Half a conveyance: a deed with nobody on one side of it.
  for (const c of conveyances) {
    if (!c.shapeId) continue;
    if (!c.from.length && !c.to.length) continue;
    if (!c.from.length) {
      say('no-grantor', 'problem', `${nameOf(byId.get(c.shapeId))} has no grantor — nothing conveys into it.`, [c.shapeId]);
    } else if (!c.to.length) {
      say('no-grantee', 'problem', `${nameOf(byId.get(c.shapeId))} has no grantee — it conveys to nothing.`, [c.shapeId]);
    }
  }

  // Conveying before taking title. The strongest finding here, because
  // it needs no judgement: a deed cannot pass what its grantor did not
  // yet hold.
  for (const party of parties) {
    const taken = took.get(party.id).filter((c) => c.date);
    if (!taken.length) continue;
    const earliest = taken.reduce((a, b) => (compareDates(a.date, b.date) <= 0 ? a : b));
    for (const out of gave.get(party.id)) {
      if (!out.date) continue;
      if (compareDates(out.date, earliest.date) < 0) {
        say(
          'out-of-order', 'problem',
          `${nameOf(party)} conveys on ${out.date.iso.slice(0, 10)} but does not take title`
          + ` until ${earliest.date.iso.slice(0, 10)}.`,
          [party.id, out.shapeId || out.edgeId, earliest.shapeId || earliest.edgeId].filter(Boolean),
        );
      }
    }
  }

  // Conveying more than was received. Only where EVERY deed on both
  // sides says what it moved — a party who took an unstated share and
  // granted a half has not been shown to have over-conveyed.
  for (const party of parties) {
    const taken = took.get(party.id);
    const given = gave.get(party.id);
    if (!taken.length || !given.length) continue;
    if (taken.some((c) => c.interest === null) || given.some((c) => c.interest === null)) continue;
    const held = taken.reduce((sum, c) => sum + c.interest, 0);
    const passed = given.reduce((sum, c) => sum + c.interest, 0);
    if (passed > held + EPSILON) {
      say(
        'over-conveyed', 'problem',
        `${nameOf(party)} conveys ${passed.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`
        + ` but took only ${held.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}.`,
        [party.id, ...given.map((c) => c.shapeId || c.edgeId).filter(Boolean)],
      );
    }
  }

  // A chain that runs in a circle. Almost always a line drawn the wrong
  // way round, and it makes every other reading of the chart wrong.
  for (const loop of cycleThrough(parties, conveyances)) {
    say(
      'loop', 'problem',
      `Title runs in a circle: ${loop.map((id) => nameOf(byId.get(id))).join(' → ')}.`,
      loop,
    );
  }

  // Where the chain stops at the top. Not an error — every chart has a
  // root — but the list of them is the list of boxes an examiner has to
  // justify, and on a chart with several it is the first thing to look at.
  const rootless = parties.filter((p) => gave.get(p.id).length && !took.get(p.id).length);
  if (rootless.length) {
    say(
      'no-source', rootless.length > 1 ? 'check' : 'note',
      rootless.length === 1
        ? `${nameOf(rootless[0])} conveys but takes title from nothing on this chart.`
        : `${rootless.length} parties convey but take title from nothing on this chart:`
          + ` ${rootless.map(nameOf).join(', ')}.`,
      rootless.map((p) => p.id),
    );
  }

  // Boxes joined to nothing at all. Usually a note, sometimes a party
  // somebody meant to connect and did not.
  const loose = parties.filter((p) => !took.get(p.id).length && !gave.get(p.id).length);
  if (loose.length) {
    say(
      'not-joined', 'note',
      loose.length === 1
        ? `${nameOf(loose[0])} is not joined to the chain.`
        : `${loose.length} boxes are not joined to the chain: ${loose.map(nameOf).join(', ')}.`,
      loose.map((p) => p.id),
    );
  }

  // Who holds it now. The answer the whole chart exists to give.
  const holders = parties.filter((p) => took.get(p.id).length && !gave.get(p.id).length);
  if (holders.length) {
    say(
      'holds-title', 'note',
      `Title ends with ${holders.map(nameOf).join(', ')}.`,
      holders.map((p) => p.id),
    );
  }

  const rank = { problem: 0, check: 1, note: 2 };
  return findings.sort((a, b) => rank[a.level] - rank[b.level]);
}

/** One line for the top of the panel. */
export function checkSummary(findings) {
  const problems = findings.filter((f) => f.level === 'problem').length;
  const checks = findings.filter((f) => f.level === 'check').length;
  if (!problems && !checks) return 'Nothing to answer for — the chain reads straight through.';
  const bits = [];
  if (problems) bits.push(`${problems} problem${problems === 1 ? '' : 's'}`);
  if (checks) bits.push(`${checks} to check`);
  return `${bits.join(' and ')}.`;
}
